import type { State } from '../State';
import * as Fate from '../fate';
import getFeatures, { Features } from '../ruleset/features';
import { isOnlineForVisit } from './visit.presence';
import type { Presence } from '@play-co/replicant';

/**
 * Check if the player is currently visiting a friend's pet
 * @return true if the player is visiting, false otherwise
 */
export const isVisiting = (state: State) => !!state.visit.visitSession;

/**
 * Whether a friend may still be visiting the player's pet at login.
 *
 * A friend can only start a visit while the player is offline, and the start is recorded in
 * `recentVisitsFrom` before login completes. Visits that started before the previous session
 * were already looked up at that session's login, so only later visits need checking.
 */
export function mayHaveCurrentVisitors(state: State): boolean {
  return Object.values(state.visit.recentVisitsFrom).some(
    (visitedAt) => visitedAt >= state.previousSessionAt,
  );
}

/**
 * Check if player has met unlock criteria for Visit feature
 * @returns
 */
export function hasMetUnlockCriteria(state: State): boolean {
  return (
    // After reborn friend visit should be unlocked
    state.petRaised >= 1 ||
    // Unlocked if pet has evolved at least once.
    state.pet.evolutions.length > 1
  );
}

/**
 * Check if feature is unlocked
 * @returns
 */
export const isUnlocked = (state: State, features?: Features) => {
  if (!(features || getFeatures(state)).visitFriend) {
    // Feature is disabled
    return false;
  }

  return hasMetUnlockCriteria(state);
};

/**
 * Get count of times player has visited a friend
 * @param state Player state
 * @param friendId the friend visited by the player
 * @return the visit count
 */
export function getVisitsCount(state: State, friendId: string): number {
  const inRecentVisited = friendId in state.visit.recentVisitsTo;
  const initValue = inRecentVisited ? 1 : 0;

  return state.visit.counts.visitsTo[friendId] ?? initValue;
}

/**
 * Gets the timestamp of the most recent time you visited a friend
 * @param state Player state
 */
export const getLastVisitTimestamp = (state: State) => {
  return state.visit.lastVisitAt;
};

/**
 * Get count of times player has been visited by a friend
 * @param state Player state
 * @param friendId the friend visiting the player
 * @return the visited count
 */
export function getVisitedCount(state: State, friendId: string): number {
  const inRecentVisitors = friendId in state.visit.recentVisitsFrom;
  const initValue = inRecentVisitors ? 1 : 0;

  return state.visit.counts.visitsFrom[friendId] ?? initValue;
}

/**
 * If the friend invited or was invited by the user.
 */
export function isURLFriend(state: State, friendId: string): boolean {
  if (state.social.referral.invitedBy === friendId) {
    return true;
  }

  return !!state.social.referral.referred[friendId];
}

/**
 * Check if user can visit another player
 * @param state The visiting (own) player.
 * @param other The other player, as their fetched record (`{ state, presence }`).
 * @param now
 * @returns
 */
export function canVisit(
  state: State,
  other: { state: State; presence?: Presence },
  now: number,
): boolean {
  const otherState = other.state;

  // Cannot visit yourself
  if (state.id === otherState.id) {
    return false;
  }

  // Check if the feature is locked for the user
  if (!isUnlocked(state)) {
    return false;
  }

  // Cannot visit while asleep
  if (Fate.getters.hasPermanentStatusEffect(state, 'asleep', now)) {
    return false;
  }

  // Cannot visit while the other player is asleep
  if (Fate.getters.hasPermanentStatusEffect(otherState, 'asleep', now)) {
    return false;
  }

  // Cannot visit if in daycare
  if (Fate.getters.hasPermanentStatusEffect(state, 'daycare', now)) {
    return false;
  }

  // Cannot visit if the other player is in daycare
  if (Fate.getters.hasPermanentStatusEffect(otherState, 'daycare', now)) {
    return false;
  }

  // Cannot visit if the other player is online.
  if (isOnlineForVisit(other.presence)) {
    return false;
  }

  // Passed all checks, can visit
  return true;
}
